import { useCallback, useEffect, useMemo, useState } from 'react';
import { uploadFiles } from 'redux/utils/fileUploader';

export interface AttachmentUploadResult {
  status: 'ok' | 'aborted' | 'failed';
  fileIds: string[];
}

export interface UseMessageAttachmentsResult {
  selectedFiles: File[];
  setSelectedFiles: (files: File[]) => void;
  removeFile: (file: File) => void;
  removeExistingFile: (fileId: string) => void;
  retainedFileIds: string[];
  isUploading: boolean;
  uploadProgress: number;
  cancelUpload: () => boolean;
  uploadAttachments: (options?: {
    trackProgress?: boolean;
  }) => Promise<AttachmentUploadResult>;
}

/**
 * Owns attachment selection and upload state for the message dialog: files staged
 * for upload, existing files marked for removal, and progress/abort handling.
 * Aborts an in-flight upload when the dialog unmounts.
 * @param existingFileIds - File ids already attached to the message being edited.
 * @returns Attachment state plus helpers to remove files and upload the staged ones.
 */
export const useMessageAttachments = (
  existingFileIds?: string[]
): UseMessageAttachmentsResult => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortController?.abort();
    };
  }, [abortController]);

  const retainedFileIds = useMemo(
    () => (existingFileIds ?? []).filter(id => !filesToRemove.includes(id)),
    [existingFileIds, filesToRemove]
  );

  const removeFile = useCallback((file: File) => {
    setSelectedFiles(current => current.filter(f => f !== file));
  }, []);

  const removeExistingFile = useCallback((fileId: string) => {
    setFilesToRemove(current => [...current, fileId]);
  }, []);

  /* Returns false when there is nothing to cancel so callers can close instead. */
  const cancelUpload = useCallback(() => {
    if (!isUploading || !abortController) {
      return false;
    }
    abortController.abort();
    setIsUploading(false);
    setUploadProgress(0);
    setAbortController(null);
    return true;
  }, [abortController, isUploading]);

  const uploadAttachments = useCallback(
    async ({ trackProgress = false } = {}): Promise<AttachmentUploadResult> => {
      if (!selectedFiles.length) {
        return { status: 'ok', fileIds: retainedFileIds };
      }

      const controller = trackProgress ? new AbortController() : null;
      if (trackProgress) {
        setIsUploading(true);
        setUploadProgress(0);
        setAbortController(controller);
      }

      try {
        const files = await uploadFiles(
          selectedFiles,
          trackProgress
            ? { onProgress: setUploadProgress, abortController: controller }
            : undefined
        );
        return {
          status: 'ok',
          fileIds: [...retainedFileIds, ...files.map(file => file.id)],
        };
      } catch (uploadError) {
        const aborted =
          uploadError instanceof Error &&
          uploadError.message === 'Upload aborted';
        return { status: aborted ? 'aborted' : 'failed', fileIds: [] };
      } finally {
        if (trackProgress) {
          setIsUploading(false);
          setUploadProgress(0);
          setAbortController(null);
        }
      }
    },
    [retainedFileIds, selectedFiles]
  );

  return {
    selectedFiles,
    setSelectedFiles,
    removeFile,
    removeExistingFile,
    retainedFileIds,
    isUploading,
    uploadProgress,
    cancelUpload,
    uploadAttachments,
  };
};
